import type { GlossaryTerm, ListGlossaryTermsQuery } from '@idpe/schema';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Badge } from '~/components/ui/Badge.js';
import { Button } from '~/components/ui/Button.js';
import { ConfirmDialog } from '~/components/ui/Modal.js';
import { DataTable, PaginationControls } from '~/components/ui/DataTable.js';
import { Field } from '~/components/ui/Field.js';
import { Select } from '~/components/ui/Select.js';
import { GlossaryFormDialog } from '~/features/glossary/GlossaryFormDialog.js';
import { useDeleteGlossaryTerm, useGlossaryList } from '~/features/glossary/hooks.js';
import { ApiError } from '~/lib/api.js';

const searchSchema = z.object({
  q: z.string().optional(),
  publicado: z
    .string()
    .optional()
    .transform((v) => (v === 'true' ? true : v === 'false' ? false : undefined)),
  page: z.coerce.number().int().min(1).default(1).optional(),
  perPage: z.coerce.number().int().min(1).max(200).default(100).optional(),
});

export const Route = createFileRoute('/_auth/glossary')({
  validateSearch: (search) => searchSchema.parse(search),
  component: GlossaryListPage,
});

function GlossaryListPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  const [editing, setEditing] = useState<GlossaryTerm | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<GlossaryTerm | null>(null);
  const deleteMut = useDeleteGlossaryTerm();

  const query: Partial<ListGlossaryTermsQuery> = {
    q: search.q,
    publicado: search.publicado,
    page: search.page ?? 1,
    perPage: search.perPage ?? 100,
  };

  const { data, isLoading, error, refetch } = useGlossaryList(query);

  function updateSearch(patch: Partial<typeof search>): void {
    navigate({
      to: '/glossary',
      search: { ...search, ...patch, page: patch.page ?? 1 },
    });
  }

  async function onConfirmDelete(): Promise<void> {
    if (!confirmDelete) return;
    try {
      await deleteMut.mutateAsync(confirmDelete.slug);
      toast.success(`Termo "${confirmDelete.slug}" removido.`);
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.displayMessage : 'Erro ao remover termo.');
    }
  }

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ O site</p>
        <h1 className="page-title">Glossário</h1>
        <p className="page-sub">
          As definições de termos de PPPs e Concessões usadas ao longo do site. Só os termos
          publicados ficam visíveis para quem visita; os demais seguem em rascunho aqui.
        </p>
      </header>

      <section>
        <div className="toolbar">
          <div className="toolbar__row">
            <div className="toolbar__filters">
              <Field
                label="Buscar"
                placeholder="termo ou slug"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') updateSearch({ q: searchInput || undefined });
                }}
                onBlur={() => updateSearch({ q: searchInput || undefined })}
              />
              <Select
                label="Status"
                value={typeof search.publicado === 'boolean' ? String(search.publicado) : ''}
                onChange={(e) =>
                  updateSearch({
                    publicado:
                      e.target.value === 'true'
                        ? true
                        : e.target.value === 'false'
                          ? false
                          : undefined,
                  })
                }
                options={[
                  { value: '', label: 'Todos' },
                  { value: 'true', label: 'Publicados' },
                  { value: 'false', label: 'Rascunhos' },
                ]}
              />
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button variant="primary" onClick={() => setCreating(true)} fullWidth>
                  Novo termo
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 'var(--s-5)' }}>
          <DataTable
            data={data?.data ?? []}
            loading={isLoading}
            error={
              error
                ? {
                    message: error.displayMessage,
                    retry: () => void refetch(),
                  }
                : null
            }
            keyExtractor={(t) => t.slug}
            onRowClick={(t) => setEditing(t)}
            emptyMessage="Nenhum termo encontrado com esses filtros."
            emptyAction={
              <Button variant="secondary" onClick={() => setCreating(true)}>
                Criar primeiro termo
              </Button>
            }
            columns={[
              {
                key: 'termo',
                header: 'Termo',
                mobileLine: 'primary',
                cell: (t) => <strong>{t.termo}</strong>,
              },
              {
                key: 'slug',
                header: 'Slug',
                mobileLine: 'secondary',
                cell: (t) => (
                  <code style={{ fontSize: 'var(--fs-sm)', color: 'var(--fg-muted)' }}>
                    {t.slug}
                  </code>
                ),
              },
              {
                key: 'relacionados',
                header: 'Relacionados',
                mobileLine: 'meta',
                cell: (t) =>
                  t.relacionados.length > 0 ? (
                    <span style={{ color: 'var(--fg-muted)' }}>{t.relacionados.length}</span>
                  ) : (
                    <span style={{ color: 'var(--fg-muted)' }}>—</span>
                  ),
              },
              {
                key: 'publicado',
                header: 'Status',
                mobileLine: 'meta',
                cell: (t) =>
                  t.publicado ? (
                    <Badge tone="success">publicado</Badge>
                  ) : (
                    <Badge tone="muted">rascunho</Badge>
                  ),
              },
              {
                key: 'actions',
                header: '',
                cell: (t) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDelete(t);
                    }}
                  >
                    Remover
                  </Button>
                ),
              },
            ]}
          />
        </div>

        {data && data.total > 0 && data.totalPages > 1 && (
          <PaginationControls
            page={data.page}
            perPage={data.perPage}
            total={data.total}
            totalPages={data.totalPages}
            onPageChange={(page) => updateSearch({ page })}
          />
        )}
      </section>

      <GlossaryFormDialog open={creating} onClose={() => setCreating(false)} />
      <GlossaryFormDialog open={editing !== null} onClose={() => setEditing(null)} term={editing} />
      <ConfirmDialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => void onConfirmDelete()}
        title={`Remover "${confirmDelete?.termo ?? ''}"?`}
        description={`O termo "${confirmDelete?.slug ?? ''}" será apagado permanentemente. Esta ação é irreversível.`}
        confirmLabel="Remover"
        variant="danger"
        loading={deleteMut.isPending}
      />
    </div>
  );
}
